import { makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components';
import {
    BarChart,
    CalendarMonth,
    Checklist,
    Group,
    LeftPanelClose,
    LeftPanelOpen,
    Settings,
} from '@material-symbols-svg/react/rounded/w500';
import type { ComponentType, SVGProps } from 'react';
import { NavLink, useLocation } from 'react-router';

import { toSlug, useCurrentMonth } from '../calendar';
import { useSettings } from '../settings';
import { palette, tokens } from '../theme';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

type NavItem = {
    key: string;
    label: string;
    icon: IconType;
    to: string;
    matchPrefix: string;
};

type Props = {
    open: boolean;
    onToggle: () => void;
};

export const Sidebar: React.FC<Props> = ({ open, onToggle }) => {
    const classes = useStyles();
    const location = useLocation();
    const { configured } = useSettings();
    const { current } = useCurrentMonth();
    const slug = toSlug(current);

    const items: NavItem[] = [
        { key: 'calendar', label: 'Calendar', icon: CalendarMonth, to: `/calendar/${slug}`, matchPrefix: '/calendar' },
        { key: 'report', label: 'Report', icon: BarChart, to: `/report/${slug}`, matchPrefix: '/report' },
        { key: 'team', label: 'Team', icon: Group, to: '/team', matchPrefix: '/team' },
        { key: 'tasks', label: 'Tasks', icon: Checklist, to: '/tasks', matchPrefix: '/tasks' },
        { key: 'settings', label: 'Settings', icon: Settings, to: '/settings', matchPrefix: '/settings' },
    ];

    const visible = configured ? items : items.filter((i) => i.key === 'settings');

    return (
        <aside className={mergeClasses(classes.root, open ? classes.open : classes.collapsed)}>
            <div className={classes.brandRow}>
                <span className={classes.brand} aria-hidden={!open}>
                    <BrandMark />
                    {open && <span className={classes.brandText}>NUXNI</span>}
                </span>
            </div>

            <nav className={classes.nav}>
                {visible.map((item) => {
                    const isActive = location.pathname.startsWith(item.matchPrefix);
                    const Icon = item.icon;
                    const link = (
                        <NavLink
                            key={item.key}
                            to={item.to}
                            className={mergeClasses(classes.navItem, isActive && classes.navItemActive, !open && classes.navItemCollapsed)}
                            aria-label={item.label}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            <Icon className={classes.navIcon} aria-hidden />
                            {open && <span className={classes.navLabel}>{item.label}</span>}
                        </NavLink>
                    );
                    if (open) {
                        return link;
                    }
                    return (
                        <Tooltip key={item.key} content={item.label} relationship="label" positioning="after">
                            {link}
                        </Tooltip>
                    );
                })}
            </nav>

            <div className={classes.footer}>
                <button
                    type="button"
                    className={mergeClasses(classes.toggleBtn, !open && classes.toggleBtnCollapsed)}
                    onClick={onToggle}
                    aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                    {open ? (
                        <LeftPanelClose className={classes.toggleIcon} aria-hidden />
                    ) : (
                        <LeftPanelOpen className={classes.toggleIcon} aria-hidden />
                    )}
                    {open && <span className={classes.toggleLabel}>Collapse</span>}
                </button>
            </div>
        </aside>
    );
};

const BrandMark: React.FC = () => {
    const classes = useStyles();
    return (
        <span className={classes.brandMark} aria-hidden>
            <svg width="20" height="20" viewBox="0 0 32 32">
                <rect width="32" height="32" rx="6" fill={palette.brandGreen} />
                <path d="M16 7.5a8.5 8.5 0 1 0 8.5 8.5h-2a6.5 6.5 0 1 1-6.5-6.5V7.5z" fill={palette.primaryText} />
                <rect x="15" y="9" width="2" height="8" rx="1" fill={palette.primaryText} />
                <rect x="16" y="15" width="6" height="2" rx="1" fill={palette.primaryText} />
            </svg>
        </span>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: palette.panelBg,
        borderRight: `1px solid ${palette.cardBorder}`,
        flexShrink: 0,
        transition: 'width 180ms ease',
        overflow: 'hidden',
    },
    open: {
        width: '220px',
    },
    collapsed: {
        width: '64px',
    },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '52px',
        padding: `0 ${tokens.spacingHorizontalM}`,
        borderBottom: `1px solid ${palette.cardBorder}`,
        flexShrink: 0,
    },
    brand: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        overflow: 'hidden',
    },
    brandMark: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        flexShrink: 0,
    },
    brandText: {
        fontSize: '15px',
        fontWeight: tokens.fontWeightBold,
        letterSpacing: '1px',
        color: tokens.colorNeutralForeground1,
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px 16px',
        borderRadius: '14px',
        textDecoration: 'none',
        color: tokens.colorNeutralForeground2,
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.accentBg,
            color: palette.accentText,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '-2px',
        },
    },
    navItemCollapsed: {
        justifyContent: 'center',
        padding: '14px 0',
    },
    navItemActive: {
        backgroundColor: palette.accentBg,
        color: palette.accentText,
        '&:hover': {
            backgroundColor: palette.accentBgHover,
            color: palette.accentText,
        },
    },
    navIcon: {
        width: '22px',
        height: '22px',
        flexShrink: 0,
    },
    navLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    footer: {
        padding: tokens.spacingHorizontalS,
        borderTop: `1px solid ${palette.cardBorder}`,
        flexShrink: 0,
    },
    toggleBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        padding: `10px 12px`,
        border: 'none',
        background: 'transparent',
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        fontSize: '13px',
        fontWeight: tokens.fontWeightSemibold,
        fontFamily: 'inherit',
        '&:hover': {
            backgroundColor: palette.panelMuted,
            color: tokens.colorNeutralForeground1,
        },
    },
    toggleBtnCollapsed: {
        justifyContent: 'center',
        padding: '10px 0',
    },
    toggleIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
    },
    toggleLabel: {
        whiteSpace: 'nowrap',
    },
});
